import { useCallback, useState } from 'react';
import { DialogContent } from '@material-ui/core';

import { CustomDialog } from 'src/components/CustomDialog';
import { FieldCheckbox } from 'src/components/FieldCheckbox';
import { FieldInput } from 'src/components/FieldInput';
import { FieldLabel } from 'src/components/FieldLabel';
import { FieldSelect } from 'src/components/FieldSelect';
import { FieldSelectMenuItem } from 'src/components/FieldSelectMenuItem';
import { GenerationToolItem } from 'src/components/GenerationToolItem';
import { PromptInstructions } from 'src/components/PromptInstructions';
import { GENERATION_TOOL } from 'src/data/generationTool';
import { isFreestyleGenerationTool, isPresetFreestyleToolId } from 'src/data/generationTool';
import { EVENT_PAGE } from 'src/lib/events';
import { useEffectOnce } from 'src/lib/hooks';
import { ROUTE_PARAMS, ROUTES } from 'src/routes';
import { useBrandBriefsMenuItems, useHistoryPushOrInfinityOpen } from 'src/services';
import { usePreparePromptInstructionsProps } from 'src/services/prompts';
import { useBrandVoicesActions, useBrandVoicesAssets } from 'src/store';

import { useEntityState } from './useEntityState';
import { useFilteredTools } from './useFilteredTools';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';
import { useViewState } from './useViewState';

export const PopupFreestyleToolSave = ({
  freestyleToolContent,
  isFreestyleToolSaveViewVisible,
  onSavePrompt,
  onClosePrompt,
  createFromScratch,
  defaultGenerationTool,
  isDocumentEditor,
}) => {
  const styles = useStyles();
  const historyPushOrInfinityOpen = useHistoryPushOrInfinityOpen();
  const { savingFreestyleTool, currentFreestyleTool, errors, generationTool, currentUrls } =
    useViewState();
  const [selectedTool, setSelectedTool] = useState(
    defaultGenerationTool
      ? GENERATION_TOOL.freestyle
      : currentFreestyleTool?.generationTool || generationTool || GENERATION_TOOL.freestyle
  );
  const error = errors?.Name;
  if (!freestyleToolContent && currentFreestyleTool) {
    freestyleToolContent = currentFreestyleTool;
  }

  const {
    entity,
    handleToolChange,
    handleNameChange,
    nameCounter,
    handlePromptChange,
    handleAllWorkspacesChange,
  } = useEntityState(freestyleToolContent, isFreestyleToolSaveViewVisible, selectedTool);
  const supportedTools = useFilteredTools();

  const {
    handleSaveFreestyleTool,
    handleCloseSavePopup,
    handleEditFreestyleTool,
    handleLastPromptUrl,
  } = useHandlers(entity, onSavePrompt, onClosePrompt, selectedTool);

  const promptInstructionsProps = usePreparePromptInstructionsProps({
    eventPage: EVENT_PAGE.templateBuilder,
    generationTool,
    currentUrls,
    provideUrlResources: true,
  });
  const { brandVoicesAssets, loadingBrandVoiceAssets } = useBrandVoicesAssets();

  const brandVoicesActions = useBrandVoicesActions();

  useEffectOnce(() => {
    brandVoicesActions.queryBrandVoicesAssets();
  });

  const handleEditClick = useCallback(
    (type = 'messaging') => {
      if (type === 'resources') {
        historyPushOrInfinityOpen(`${ROUTES.INTEGRATIONS_RESOURCES}/?${ROUTE_PARAMS.resources}`);
      } else if (type === 'messaging') {
        historyPushOrInfinityOpen(
          `${ROUTES.BRAND_VOICE}/${brandVoicesAssets[0].id}${ROUTES.BRAND_BRIEFS}`
        );
      }
    },
    [brandVoicesAssets, historyPushOrInfinityOpen]
  );

  const brandBriefsMenuItems = useBrandBriefsMenuItems({
    brandVoicesAssets,
    isLoading: loadingBrandVoiceAssets,
    onEditBrief: useCallback(() => {
      handleEditClick('messaging');
    }, [handleEditClick]),
  });

  const disabled = savingFreestyleTool;
  const promptValue = freestyleToolContent?.allInstructionsData?.html;
  const promptSerialized = freestyleToolContent?.allInstructionsData?.serialized;
  const saveButtonDisabled =
    disabled || !entity.name || nameCounter.value > nameCounter.max || promptSerialized === '';
  // In case of preset prompt the id starts with pp, if we are saving this is creating a new one and not editing.
  const editing = !!currentFreestyleTool && !isPresetFreestyleToolId(currentFreestyleTool.id);
  const title = createFromScratch ? 'Create new prompt' : editing ? 'Update prompt' : 'Save prompt';

  return (
    <CustomDialog
      className={styles.root}
      title={title}
      onClose={handleCloseSavePopup}
      onSubmit={editing ? handleEditFreestyleTool : handleSaveFreestyleTool}
      onCancel={handleCloseSavePopup}
      submitLabel={createFromScratch ? 'Create' : 'Save'}
      cancelLabel="Cancel"
      disabled={disabled}
      submitDisabled={saveButtonDisabled}
      loading={savingFreestyleTool}
      dialogActionsSlot={
        !editing ? (
          <FieldCheckbox
            classes={{ label: styles.checkboxLabel }}
            label="Enable access in all workspaces"
            checked={entity.allWorkspaces}
            onChange={handleAllWorkspacesChange}
            disabled={disabled}
          />
        ) : null
      }
      data-testid="PopupFreestyleToolSave"
      open
      fullWidth
    >
      <DialogContent className={styles.dialogContent}>
        <FieldInput
          className={styles.shortField}
          label="Name"
          value={entity.name || ''}
          onChange={handleNameChange}
          error={error}
          disabled={disabled}
          placeholder="Enter your prompt name"
          InputProps={{
            autoFocus: true,
          }}
          LabelProps={{
            titleBold: true,
          }}
        />

        <FieldSelect
          className={styles.shortField}
          classes={{ menuPaper: styles.menuPaper }}
          label="Template"
          placeholder="Select..."
          value={selectedTool}
          onChange={(e) => {
            handleToolChange(e);
            setSelectedTool(e.target.value);
          }}
          renderValue={(selectedValue) => (
            <GenerationToolItem
              classes={{ label: styles.genToolLabel }}
              tool={selectedValue}
              toolTitle={isFreestyleGenerationTool(selectedValue) ? 'General' : null}
              hideIcon={isFreestyleGenerationTool(selectedValue)}
            />
          )}
          disabled={disabled}
          LabelProps={{ titleBold: true }}
        >
          {supportedTools.map((tool) => (
            <FieldSelectMenuItem
              key={tool}
              value={tool}
            >
              <GenerationToolItem
                classes={{ label: styles.genToolLabel }}
                tool={tool}
                toolTitle={isFreestyleGenerationTool(tool) ? 'General' : null}
                hideIcon={isFreestyleGenerationTool(tool)}
              />
            </FieldSelectMenuItem>
          ))}
        </FieldSelect>

        <div className={styles.field}>
          <FieldLabel
            label="Prompt"
            titleBold
          />
          <PromptInstructions
            className={styles.promptField}
            classes={{ input_root: styles.textArea }}
            editableClasses={{ editable: styles.editable }}
            {...promptInstructionsProps}
            value={promptValue}
            onChange={handlePromptChange}
            brandBriefsMenuItems={brandBriefsMenuItems}
            generationTool={selectedTool}
            onLastPromptUrl={handleLastPromptUrl}
            noMinMaxHeight
            hideActions
            isSavePopup
            hideImages
          />
        </div>
      </DialogContent>
    </CustomDialog>
  );
};
